import { EmptyResponseEntity } from "types/emptyResponseEntity";
import { BTLoginTypes } from "types/enum";

import { APIHandler } from "utilities/apiHandler";

import { IDTypes } from "commonComponents/entity/accounting/common/accounting.api.types";
import {
    LinkedAccountingEntityResponse,
    UnlinkAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import { JobAccessEntity } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";

import { type IFilterFormValues } from "entity/filters/Filter/Filter.api.types";
import { getNullableFilterString } from "entity/filters/filters.utils";
import {
    type IInternalUserDeleteConflictsFormValues,
    InternalUserDeleteConflictsEntity,
} from "entity/internalUser/InternalUserDeleteConflicts/InternalUserDeleteConflicts.api.types";
import { PayrollUpdateResponse } from "entity/payroll/PayrollSettings/PayrollSettings.api.types";

import {
    AccountingUserSyncResponse,
    InternalUserEntity,
    type InternalUserEntitySaveRequest,
    InternalUsersPayrollSettingsResponse,
} from "./InternalUser.api.types";

export interface IInternalUserHandler {
    get(id: number): Promise<InternalUserEntity>;
    getDefault(): Promise<InternalUserEntity>;
    getJobsitesForUser(
        userId: number,
        filter?: IFilterFormValues,
        addToAllExistingJobs?: boolean,
    ): Promise<JobAccessEntity>;
    getAccountingInfo(userId: number): Promise<LinkedAccountingEntityResponse>;
    syncEmployee(userId: number): Promise<AccountingUserSyncResponse>;
    unlinkAccountingVendor(userId: number): Promise<UnlinkAccountingResponse>;
    create(newValues: InternalUserEntitySaveRequest): Promise<InternalUserEntity>;
    update(id: number, updateRequest: InternalUserEntitySaveRequest): Promise<InternalUserEntity>;
    delete(id: number): Promise<EmptyResponseEntity>;
    deleteCalendarFeed(id: number, feedId: number): Promise<EmptyResponseEntity>;
    getDeleteConflicts(id: number): Promise<InternalUserDeleteConflictsEntity>;
    reassignJobsitesForUserDeletion(
        oldLoginId: number,
        newLoginId: number,
    ): Promise<InternalUserDeleteConflictsEntity>;
    reassignItemsForUserDeletion(
        formValues: IInternalUserDeleteConflictsFormValues,
    ): Promise<InternalUserDeleteConflictsEntity>;
    getPayrollSettingsInfo(userId: number): Promise<InternalUsersPayrollSettingsResponse>;
    addToGusto(userId: number): Promise<PayrollUpdateResponse>;
    syncWithGusto(userId: number): Promise<PayrollUpdateResponse>;
    disconnectFromGusto(userId: number): Promise<PayrollUpdateResponse>;
}

export class InternalUserHandler implements IInternalUserHandler {
    async get(id: number): Promise<InternalUserEntity> {
        return await APIHandler(`/api/InternalUsers/${id}`, {
            method: "GET",
            responseType: InternalUserEntity,
        });
    }

    async getDefault(): Promise<InternalUserEntity> {
        return await APIHandler(`/api/InternalUsers/Defaults`, {
            method: "GET",
            responseType: InternalUserEntity,
        });
    }

    async getJobsitesForUser(
        userId: number,
        filter?: IFilterFormValues,
        addToAllExistingJobs?: boolean,
    ): Promise<JobAccessEntity> {
        return await APIHandler(`/api/InternalUsers/JobAccess`, {
            method: "GET",
            data: { userId, filter: getNullableFilterString(filter), addToAllExistingJobs },
            responseType: JobAccessEntity,
        });
    }

    async getAccountingInfo(userId: number) {
        return await APIHandler(
            `/api/Accounting/${userId}/LinkedEntityInfo?loginType=${BTLoginTypes.BUILDER}`,
            {
                method: "GET",
                responseType: LinkedAccountingEntityResponse,
            },
        );
    }

    async syncEmployee(userId: number): Promise<AccountingUserSyncResponse> {
        return await APIHandler(`/api/Accounting/${userId}/SyncEmployee`, {
            method: "PUT",
            responseType: AccountingUserSyncResponse,
        });
    }

    async unlinkAccountingVendor(id: number): Promise<UnlinkAccountingResponse> {
        return await APIHandler(`/api/Accounting/AccountingEntity`, {
            method: "PUT",
            data: {
                accountingEntityID: "-1",
                idType: IDTypes.EmployeeID,
                numericEntityID: id,
            },
            responseType: UnlinkAccountingResponse,
        });
    }

    async create(newValues: InternalUserEntitySaveRequest): Promise<InternalUserEntity> {
        return await APIHandler(`/api/InternalUsers/AddInternalUserReact`, {
            method: "POST",
            data: newValues,
            responseType: InternalUserEntity,
        });
    }

    async update(
        id: number,
        updateRequest: InternalUserEntitySaveRequest,
    ): Promise<InternalUserEntity> {
        return await APIHandler(`/api/InternalUsers/${id}/UpdateInternalUserReact`, {
            method: "PUT",
            data: updateRequest,
            responseType: InternalUserEntity,
        });
    }

    async delete(id: number): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/InternalUsers/${id}`, {
            method: "DELETE",
            responseType: EmptyResponseEntity,
        });
    }

    async deleteCalendarFeed(id: number, feedId: number): Promise<EmptyResponseEntity> {
        return await APIHandler(
            `/api/Calendar/DeleteCalendarFeed?calendarFeedId=${feedId}&loginId=${id}`,
            { method: "DELETE", responseType: EmptyResponseEntity },
        );
    }

    async getDeleteConflicts(id: number): Promise<InternalUserDeleteConflictsEntity> {
        return await APIHandler(`/api/InternalUsers/${id}/InternalUserDeleteConflicts`, {
            method: "GET",
            responseType: InternalUserDeleteConflictsEntity,
        });
    }

    async reassignJobsitesForUserDeletion(
        oldLoginId: number,
        newLoginId: number,
    ): Promise<InternalUserDeleteConflictsEntity> {
        return await APIHandler(`/api/InternalUsers/ReassignJobsitesForUserDeletion`, {
            method: "PUT",
            data: { oldLoginId, newLoginId },
            responseType: InternalUserDeleteConflictsEntity,
        });
    }

    async reassignItemsForUserDeletion(
        formValues: IInternalUserDeleteConflictsFormValues,
    ): Promise<InternalUserDeleteConflictsEntity> {
        return await APIHandler(`/api/InternalUsers/ReassignItemsForUserDeletion`, {
            method: "PUT",
            data: formValues,
            responseType: InternalUserDeleteConflictsEntity,
        });
    }

    async getPayrollSettingsInfo(globalUserId: number) {
        if (globalUserId === 0) {
            throw new Error("globalUserId cannot be 0");
        }

        return await APIHandler(`/api/payroll/InternalUsersSettings?globalUserId=${globalUserId}`, {
            method: "GET",
            responseType: InternalUsersPayrollSettingsResponse,
        });
    }

    async syncWithGusto(globalUserId: number): Promise<PayrollUpdateResponse> {
        if (globalUserId === 0) {
            throw new Error("globalUserId cannot be 0");
        }

        return await APIHandler("/api/payroll/SyncEmployee", {
            method: "PUT",
            data: {
                id: globalUserId,
            },
            responseType: PayrollUpdateResponse,
        });
    }

    async addToGusto(globalUserId: number): Promise<PayrollUpdateResponse> {
        if (globalUserId === 0) {
            throw new Error("globalUserId cannot be 0");
        }

        return await APIHandler("/api/payroll/CreateEmployee", {
            method: "PUT",
            data: {
                id: globalUserId,
            },
            responseType: PayrollUpdateResponse,
        });
    }

    async disconnectFromGusto(globalUserId: number): Promise<PayrollUpdateResponse> {
        if (globalUserId === 0) {
            throw new Error("globalUserId cannot be 0");
        }

        return await APIHandler("/api/payroll/DisconnectEmployee", {
            method: "PUT",
            data: {
                id: globalUserId,
            },
            responseType: PayrollUpdateResponse,
        });
    }
}
